import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useGetRfids() {
  const [rfids, setRfids] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const { send, isConnected, connect, registerHandler } =
    useWebSocketContext();

  const mountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);

  const fetchRfids = useCallback(
    async (attempt = 0) => {
      if (!mountedRef.current) return;

      setIsLoading(true);
      setError("");

      try {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }

        // Ensure WS connection
        if (!isConnected) {
          
        }

        const group_id = localStorage.getItem("group_id") || "1";

        // ✅ getter you implemented
        const response = await send("cms_get_rfids", { group_id });
        console.log("RFIDs response:", response);

        if (!response || response.type !== "cms_get_rfids_response") {
          throw new Error("Invalid response from server");
        }

        if (!Array.isArray(response.data)) {
          throw new Error("No RFID data available");
        }

        // 🔄 normalize for UI
        const normalised = response.data.map((r) => ({
          id: r.id,
          rfid: r.rfid,
          user_id: r.user_id,
          phone: r.phone, // if backend sends phone
          status: r.status,
          group_id: r.group_id,
          expiry_date: r.expiry_date,
          created_at: r.created_at,
        }));

        setRfids(normalised);
      } catch (err) {
        setError(err.message || "Failed to fetch RFIDs");

        if (mountedRef.current) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
          retryTimeoutRef.current = setTimeout(
            () => fetchRfids(attempt + 1),
            delay
          );
        }
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, connect, send]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchRfids();

    // 🔔 live updates (optional but future-proof)
    const groupId = localStorage.getItem("group_id") || "1";
    const unregister = registerHandler(
      `rfids_update_group_${groupId}`,
      (msg) => {
        if (Array.isArray(msg.data) && mountedRef.current) {
          const normalised = msg.data.map((r) => ({
            id: r.id,
            rfid: r.rfid,
            user_id: r.user_id,
            phone: r.phone,
            status: r.status,
            group_id: r.group_id,
            expiry_date: r.expiry_date,
            created_at: r.created_at,
          }));
          setRfids(normalised);
        }
      }
    );

    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      unregister();
    };
  }, [fetchRfids, registerHandler]);

  return {
    rfids,
    isLoading,
    error,
    refetch: () => fetchRfids(0),
  };
}
